import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToken } from "@/lib/session";
import { toast } from "sonner";
import { Camera, ArrowLeft, Trophy, ThumbsUp, Send, Crown } from "lucide-react";
import { api, API } from "@/lib/api";
import GameWindowBanner from "@/components/GameWindowBanner";

export default function CaptionPage() {
  const navigate = useNavigate();
  const token = useToken();
  const [contest, setContest] = useState(null);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [winners, setWinners] = useState([]);

  const refresh = useCallback(() => api.get(`/caption/today?token=${token}`).then((r) => setContest(r.data.contest)).catch((e) => console.error("caption load failed", e)), [token]);
  const refreshWinners = useCallback(() => api.get(`/caption/winners?token=${token}`).then((r) => setWinners(r.data)).catch((e) => console.error("winners load failed", e)), [token]);

  useEffect(() => {
    refresh();
    refreshWinners();
    // light polling for vote counts / new captions
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [refresh, refreshWinners]);

  const submit = async () => {
    if (!text.trim() || !contest?.id) return;
    setSubmitting(true);
    try {
      await api.post(`/caption/submit?token=${token}`, { contest_id: contest.id, text });
      toast.success("Caption submitted — go gather votes!");
      setText("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleVote = async (capId) => {
    try {
      await api.post(`/caption/vote?token=${token}`, { caption_id: capId });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vote failed");
    }
  };

  const mySubmission = contest?.captions?.find((c) => c.is_self);

  return (
    <div className="min-h-screen themed-bg">
      <header className="border-b-4 px-6 py-4 flex items-center justify-between themed-border" style={{ background: "var(--bq-header-bg)" }}>
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="brutal-btn inline-flex items-center gap-1">
          <ArrowLeft size={14} /> HUB
        </button>
        <h1 className="font-black uppercase tracking-tight text-lg themed flex items-center gap-2">
          <Camera size={20} /> Photo Caption Contest
        </h1>
        <div style={{ width: 60 }} />
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 grid gap-6">
        <GameWindowBanner game="caption" />
        {!contest ? (
          <section data-testid="caption-empty" className="brutal-card p-8 text-center">
            <Camera size={28} className="mx-auto mb-2" />
            <div className="font-black uppercase text-xl">No contest today</div>
            <p className="text-sm mt-2" style={{ color: "var(--bq-muted)" }}>{`Admin hasn't uploaded a photo yet. Come back later or peek at past winners below.`}</p>
          </section>
        ) : (
          <>
            <section data-testid="caption-contest" className="brutal-card p-4 overflow-hidden">
              {contest.title && <h2 className="text-xl font-black uppercase mb-3">{contest.title}</h2>}
              <div className="border-2 themed-border overflow-hidden mb-4" style={{ background: "var(--bq-bg)" }}>
                <img src={`${API}/files/${contest.image_path}`} alt="caption-contest" data-testid="caption-contest-image" className="w-full object-contain max-h-[420px]" />
              </div>

              {!mySubmission ? (
                <div className="grid gap-2">
                  <textarea
                    data-testid="caption-text-input"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={`Drop your wittiest caption…`}
                    maxLength={180}
                    rows={2}
                    className="border-2 themed-border p-3 font-bold"
                    style={{ background: "var(--bq-card-bg)" }}
                  />
                  <div className="flex items-center justify-between text-xs" style={{ color: "var(--bq-muted)" }}>
                    <span>{text.length}/180</span>
                    <button
                      data-testid="caption-submit-btn"
                      disabled={submitting || text.trim().length < 2}
                      onClick={submit}
                      className="brutal-btn inline-flex items-center gap-1 disabled:opacity-50"
                      style={{ background: "var(--bq-accent)" }}
                    >
                      <Send size={14} /> {submitting ? "Sending…" : "Submit caption"}
                    </button>
                  </div>
                </div>
              ) : (
                <div data-testid="caption-mine" className="border-2 themed-border p-3" style={{ background: "var(--bq-accent2)" }}>
                  <div className="text-xs uppercase font-mono-bq mb-1">Your entry</div>
                  <div className="font-bold">{`"${mySubmission.text}"`}</div>
                </div>
              )}
            </section>

            <section className="brutal-card p-5">
              <h3 className="text-lg font-black uppercase mb-3 flex items-center gap-2"><Trophy size={18} /> Live Standings</h3>
              {contest.captions.length === 0 ? (
                <p className="text-sm" style={{ color: "var(--bq-muted)" }}>Be the first to drop a caption.</p>
              ) : (
                <ol className="grid gap-2">
                  {contest.captions.map((c, i) => (
                    <li key={c.id} data-testid={`caption-row-${i}`} className="border-2 themed-border p-3 flex items-center gap-3" style={{ background: "var(--bq-card-bg)" }}>
                      <div className="w-7 h-7 border-2 themed-border inline-flex items-center justify-center font-black text-xs">{i + 1}</div>
                      {c.user.avatar_path ? (
                        <img src={`${API}/files/${c.user.avatar_path}`} alt="" className="w-8 h-8 object-cover border-2 themed-border" />
                      ) : (
                        <div className="w-8 h-8 border-2 themed-border flex items-center justify-center font-black text-xs" style={{ background: "var(--bq-accent2)" }}>
                          {(c.user.name || "?")[0]}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-bold truncate">{`"${c.text}"`}</div>
                        <div className="text-xs" style={{ color: "var(--bq-muted)" }}>{c.user.name}{c.is_self && " · you"}</div>
                      </div>
                      <button
                        data-testid={`caption-vote-${c.id}`}
                        disabled={c.is_self}
                        onClick={() => toggleVote(c.id)}
                        className={`brutal-btn inline-flex items-center gap-1 text-xs disabled:opacity-50`}
                        style={{ background: c.viewer_voted ? "var(--bq-accent)" : "var(--bq-card-bg)" }}
                      >
                        <ThumbsUp size={12} /> {c.votes}
                      </button>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </>
        )}

        <section className="brutal-card p-5">
          <h3 className="text-lg font-black uppercase mb-3 flex items-center gap-2"><Crown size={18} /> Past Winners</h3>
          {winners.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--bq-muted)" }}>The hall of fame is empty… for now.</p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {winners.map((w) => (
                <div key={w.contest_id} data-testid={`winner-${w.contest_id}`} className="border-2 themed-border p-3" style={{ background: "var(--bq-card-bg)" }}>
                  <div className="aspect-video border-2 themed-border overflow-hidden mb-2" style={{ background: "var(--bq-bg)" }}>
                    {w.image_path && <img src={`${API}/files/${w.image_path}`} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="text-xs uppercase font-mono-bq mb-1">{w.active_date}</div>
                  {w.winner_caption ? (
                    <>
                      <div className="font-bold text-sm">{`"${w.winner_caption}"`}</div>
                      <div className="text-xs" style={{ color: "var(--bq-muted)" }}>— {w.winner_name || "?"} · {w.winner_votes} 👍</div>
                    </>
                  ) : (
                    <div className="text-xs" style={{ color: "var(--bq-muted)" }}>No submissions.</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
